import React, { useCallback, MouseEvent } from 'react';
import { HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { AppointmentPet } from './AppointmentPet';
import StatusTag from 'pages/Reporting/components/StatusTag';
import { Button } from '@televet/kibble-ui';
import { closeAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';
import { useRecap } from 'shared/providers/RecapProvider/RecapProvider';
import { usePhoneCallContext } from 'shared/providers/PhoneCallProvider';
import useAppointmentChannel from 'pages/Appointments/hooks/useAppointmentChannel';
import HasModule from 'shared/components/HasModule';
import { ModuleName } from '@televet/shared-types/productPackages';

export const AppointmentModalHeader = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { stageOutgoingPhoneCall } = usePhoneCallContext();
  const { isRecapActive, setRecapAppointmentId, toggleRecap } = useRecap();

  const {
    appointmentDraft: { status, channelStatus, channelId, petParentIds, petId: clinicPetId },
    appointmentId,
  } = useAppSelector((state) => state.appointmentsModal);

  const onCloseModal = useCallback(() => {
    dispatch(closeAppointmentModal());
  }, [dispatch]);

  const onRecapClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      if (!isRecapActive && appointmentId) {
        setRecapAppointmentId(appointmentId);
        toggleRecap();
      }
    },
    [appointmentId, isRecapActive, setRecapAppointmentId, toggleRecap],
  );

  const { onChatClick } = useAppointmentChannel({
    appointmentId,
    channelId,
    channelStatusAction: channelStatus?.channelStatusAction,
    petParentIds: petParentIds || [],
    clinicPetId,
  });

  const onCallClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      const [petParentId] = petParentIds || [];
      stageOutgoingPhoneCall({ clinicPetParentId: petParentId });
      onCloseModal();
    },
    [petParentIds, stageOutgoingPhoneCall, onCloseModal],
  );

  const onOpenConversation = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      onChatClick(e);
      onCloseModal();
    },
    [onChatClick, onCloseModal],
  );

  return (
    <VStack w="100%" spacing={1}>
      <HStack justifyContent="space-between" alignItems="flex-start" w="100%">
        <AppointmentPet />
        <StatusTag label={status ?? 'unknown'} bg="background.subtle" iconName="calendarInfo" useLeftElement />
      </HStack>
      <HStack w="100%" alignItems="flex-end" justifyContent="space-between" mt={3} display="flex">
        {channelStatus && (
          <StatusTag
            label={channelStatus.name}
            bg={channelStatus.color}
            iconName="message"
            textColor="text.onContrast"
            fontWeight="bold"
            useLeftElement
          />
        )}
        <HStack spacing={1} ml="auto">
          <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
            <Button
              variant="secondarySubtle"
              size="sm"
              iconName="message"
              aria-label="Open converstation"
              onClick={onOpenConversation}
            />
            <Button
              variant="secondarySubtle"
              size="sm"
              iconName="phone"
              aria-label="Call pet parent"
              isDisabled={!petParentIds?.length}
              onClick={onCallClick}
            />
          </HasModule>
          <Button
            variant="secondarySubtle"
            size="sm"
            iconName="aiMicrophone"
            aria-label="Start AI Recap"
            onClick={onRecapClick}
          />
        </HStack>
      </HStack>
    </VStack>
  );
};
